import { Injectable } from '@angular/core';
import { IReportingService } from '../interfaces';
import { ReportingTransactionsModel, ReportingTransactionsTransformer } from '../../../models';
import { IReportingHttpService, Transactions as RawTransactionData } from '../../http';
import { Observable, defer, of } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';

@Injectable()
export class ReportingService implements IReportingService {
  private allTransactionsCache$: Observable<RawTransactionData[]> | null = null;
  private rawTransactionsCache: RawTransactionData[] = [];
  private totalTransactions = 0;
  private pageSize = 50; // Default page size, can be made configurable

  constructor(private monitoringHttpService: IReportingHttpService) {}

  async getReportingPartners(
    country: string,
    startDate: string,
    endDate: string,
    theaterCode?: string,
  ): Promise<ReportingTransactionsModel[]> {
    const partnerReporting = await this.monitoringHttpService.getReportingPartners(country, startDate, endDate, theaterCode);
    return partnerReporting.transactions.map((line: RawTransactionData) => ReportingTransactionsTransformer.parse(line));
  }

  async getReportingTransactions(
    country: string,
    startDate: string,
    endDate: string,
    theaterCode?: string,
  ): Promise<ReportingTransactionsModel[]> {
    const reportingTransactions = await this.monitoringHttpService.getReportingTransactions(country, startDate, endDate, theaterCode);
    return reportingTransactions.transactions.map((transaction: RawTransactionData) => ReportingTransactionsTransformer.parse(transaction));
  }

  getReportingTransactionsWithPagination(
    country: string,
    startDate: string,
    endDate: string,
    pageIndex: number,
    theaterCode?: string,
  ): Observable<{ data: ReportingTransactionsModel[]; total: number; hasMore: boolean }> {
    const ensureCachePopulated$ = this.ensureRawTransactionsCache(country, startDate, endDate, theaterCode, false);

    return ensureCachePopulated$.pipe(
      map(() => {
        const start = pageIndex * this.pageSize;
        const end = start + this.pageSize;
        const chunkToParse = this.rawTransactionsCache.slice(start, end);
        const parsedChunk = chunkToParse.map((transaction) => ReportingTransactionsTransformer.parse(transaction));
        return {
          data: parsedChunk,
          total: this.totalTransactions,
          hasMore: end < this.totalTransactions,
        };
      }),
    );
  }

  getReportingPartnersWithPagination(
    country: string,
    startDate: string,
    endDate: string,
    pageIndex: number,
    theaterCode?: string,
  ): Observable<{ data: ReportingTransactionsModel[]; total: number; hasMore: boolean }> {
    const ensureCachePopulated$ = this.ensureRawTransactionsCache(country, startDate, endDate, theaterCode, true);

    return ensureCachePopulated$.pipe(
      map(() => {
        const start = pageIndex * this.pageSize;
        const end = start + this.pageSize;
        const chunkToParse = this.rawTransactionsCache.slice(start, end);
        const parsedChunk = chunkToParse.map((transaction) => ReportingTransactionsTransformer.parse(transaction));
        return {
          data: parsedChunk,
          total: this.totalTransactions,
          hasMore: end < this.totalTransactions,
        };
      }),
    );
  }

  getAllParsedTransactionsForExport(
    country: string,
    startDate: string,
    endDate: string,
    isPartner: boolean,
    theaterCode?: string,
  ): Observable<ReportingTransactionsModel[]> {
    const ensureCachePopulated$ = this.ensureRawTransactionsCache(country, startDate, endDate, theaterCode, isPartner);

    return ensureCachePopulated$.pipe(
      map(() => {
        return this.rawTransactionsCache.map((transaction) => ReportingTransactionsTransformer.parse(transaction));
      }),
    );
  }

  private ensureRawTransactionsCache(
    country: string,
    startDate: string,
    endDate: string,
    theaterCode: string | undefined,
    isPartnerCall: boolean,
  ): Observable<RawTransactionData[]> {
    if (this.allTransactionsCache$ && this.rawTransactionsCache.length > 0) {
      return of(this.rawTransactionsCache);
    }
    const httpCall$ = isPartnerCall
      ? this.monitoringHttpService.getReportingPartners(country, startDate, endDate, theaterCode)
      : this.monitoringHttpService.getReportingTransactions(country, startDate, endDate, theaterCode);

    this.allTransactionsCache$ = defer(() => httpCall$).pipe(
      tap((response) => {
        this.rawTransactionsCache = response.transactions;
        this.totalTransactions = response.transactions.length;
      }),
      map((response) => response.transactions),
      shareReplay(1),
    );
    return this.allTransactionsCache$;
  }

  resetCache(): void {
    this.allTransactionsCache$ = null;
    this.rawTransactionsCache = [];
    this.totalTransactions = 0;
  }
}
